import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Auth
  login: (username, pin) => apiClient.post('/auth/login', { username, pin }),
  getMe: () => apiClient.get('/auth/me'),
  
  // Products
  getProducts: () => apiClient.get('/products'),
  searchProducts: (q, opts = {}) => apiClient.get('/products/search', { params: { q, limit: opts.limit || 20, in_stock_only: opts.inStockOnly || false } }),
  quickAddBySerial: (serial) => apiClient.get(`/serials/quick-add/${encodeURIComponent(serial)}`),
  getProduct: (id) => apiClient.get(`/products/${id}`),
  createProduct: (data) => apiClient.post('/products', data),
  updateProduct: (id, data) => apiClient.put(`/products/${id}`, data),
  deleteProduct: (id) => apiClient.delete(`/products/${id}`),
  
  // Customers
  getCustomers: () => apiClient.get('/customers'),
  createCustomer: (data) => apiClient.post('/customers', data),
  updateCustomer: (id, data) => apiClient.put(`/customers/${id}`, data),
  deleteCustomer: (id) => apiClient.delete(`/customers/${id}`),
  
  // RMAs
  getRMAs: (params) => apiClient.get('/rmas', { params }),
  createRMA: (data) => apiClient.post('/rmas', data),
  updateRMA: (id, data) => apiClient.put(`/rmas/${id}`, data),
  deleteRMA: (id) => apiClient.delete(`/rmas/${id}`),
  lookupRMAPurchase: (serial) => apiClient.get(`/rmas/lookup-purchase/${encodeURIComponent(serial)}`),
  recordRMACost: (id, data) => apiClient.post(`/rmas/${id}/record-cost`, data),
  // Supplier RMA lifecycle
  rmaSendToSupplier: (id, data) => apiClient.post(`/rmas/${id}/send-to-supplier`, data || {}),
  rmaReceiveReplacement: (id, data) => apiClient.post(`/rmas/${id}/receive-replacement`, data || {}),
  rmaReceiveRepaired: (id, data) => apiClient.post(`/rmas/${id}/receive-repaired`, data || {}),
  rmaReceiveCredit: (id, data) => apiClient.post(`/rmas/${id}/receive-credit`, data || {}),
  rmaRejectWarranty: (id, data) => apiClient.post(`/rmas/${id}/reject-warranty`, data || {}),
  rmaDeliverToCustomer: (id, data) => apiClient.post(`/rmas/${id}/deliver-to-customer`, data || {}),
  repairCreateSupplierRMA: (id, data) => apiClient.post(`/repairs/${id}/create-supplier-rma`, data || {}),
  getRMAReplacementLog: (params) => apiClient.get('/reports/rma-replacement-log', { params }),

  // PC Builds
  getBuilds: (params) => apiClient.get('/builds', { params }),
  getBuild: (id) => apiClient.get(`/builds/${id}`),
  createBuild: (data) => apiClient.post('/builds', data),
  updateBuild: (id, data) => apiClient.put(`/builds/${id}`, data),
  deleteBuild: (id) => apiClient.delete(`/builds/${id}`),
  swapBuildComponent: (id, data) => apiClient.post(`/builds/${id}/swap-component`, data),
  convertBuildToInvoice: (id, data) => apiClient.post(`/builds/${id}/convert-to-invoice`, data || {}),

  // Dealer Credit / Accounts Receivable
  recordInvoicePayment: (id, data) => apiClient.post(`/invoices/${id}/payments`, data),
  getDealersOutstanding: () => apiClient.get('/dealers/outstanding'),
  getCustomerStatement: (id) => apiClient.get(`/customers/${id}/statement`),

  // Suppliers
  getSuppliers: () => apiClient.get('/suppliers'),
  createSupplier: (data) => apiClient.post('/suppliers', data),
  updateSupplier: (id, data) => apiClient.put(`/suppliers/${id}`, data),
  deleteSupplier: (id) => apiClient.delete(`/suppliers/${id}`),
  getSupplierHistory: (id) => apiClient.get(`/suppliers/${id}/history`),

  // Serial trace (global lookup)
  traceSerial: (sn) => apiClient.get(`/serials/trace/${encodeURIComponent(sn)}`),

  // Credit Notes
  getCreditNotes: (params) => apiClient.get('/credit-notes', { params }),
  createCreditNote: (data) => apiClient.post('/credit-notes', data),
  updateCreditNote: (id, data) => apiClient.put(`/credit-notes/${id}`, data),
  deleteCreditNote: (id) => apiClient.delete(`/credit-notes/${id}`),
  getCustomerCreditBalance: (customerId) => apiClient.get(`/customers/${customerId}/credit-balance`),

  // Quotations
  getQuotations: (params) => apiClient.get('/quotations', { params }),
  createQuotation: (data) => apiClient.post('/quotations', data),
  updateQuotation: (id, data) => apiClient.put(`/quotations/${id}`, data),
  deleteQuotation: (id) => apiClient.delete(`/quotations/${id}`),
  convertQuotation: (id) => apiClient.post(`/quotations/${id}/convert-to-invoice`),

  // Expenses
  getExpenses: (params) => apiClient.get('/expenses', { params }),
  createExpense: (data) => apiClient.post('/expenses', data),
  updateExpense: (id, data) => apiClient.put(`/expenses/${id}`, data),
  deleteExpense: (id) => apiClient.delete(`/expenses/${id}`),
  approveExpense: (id, status) => apiClient.post(`/expenses/${id}/approve`, { status }),
  getExpenseCategories: () => apiClient.get('/expenses/categories'),
  addExpenseCategory: (name) => apiClient.post('/expenses/categories', { name }),
  getProfitLoss: (params) => apiClient.get('/reports/profit-loss', { params }),
  
  // Repairs
  getRepairs: () => apiClient.get('/repairs'),
  createRepair: (data) => apiClient.post('/repairs', data),
  updateRepair: (id, data) => apiClient.put(`/repairs/${id}`, data),
  deleteRepair: (id) => apiClient.delete(`/repairs/${id}`),
  
  // Invoices
  getInvoices: () => apiClient.get('/invoices'),
  createInvoice: (data) => apiClient.post('/invoices', data),
  updateInvoice: (id, data) => apiClient.put(`/invoices/${id}`, data),
  deleteInvoice: (id) => apiClient.delete(`/invoices/${id}`),
  
  // GRNs
  getGRNs: () => apiClient.get('/grns'),
  createGRN: (data) => apiClient.post('/grns', data),
  updateGRN: (id, data) => apiClient.put(`/grns/${id}`, data),
  deleteGRN: (id) => apiClient.delete(`/grns/${id}`),
  
  // Users
  getUsers: () => apiClient.get('/users'),
  getActiveUsers: () => apiClient.get('/users/active'),
  createUser: (data) => apiClient.post('/users', data),
  updateUser: (id, data) => apiClient.put(`/users/${id}`, data),
  deleteUser: (id) => apiClient.delete(`/users/${id}`),
  
  // Blacklist
  getBlacklist: () => apiClient.get('/blacklist'),
  createBlacklist: (data) => apiClient.post('/blacklist', data),
  deleteBlacklist: (id) => apiClient.delete(`/blacklist/${id}`),
  
  // Activity
  getActivity: () => apiClient.get('/activity'),
  
  // Stats
  getStats: () => apiClient.get('/stats'),
  
  // Settings
  getSettings: () => apiClient.get('/settings'),
  updateSettings: (data) => apiClient.put('/settings', data),
  
  // Reports
  getSalesReport: (params) => apiClient.get('/reports/sales', { params }),
  getTopProducts: (limit = 10) => apiClient.get('/reports/top-products', { params: { limit } }),
  getInventoryReport: () => apiClient.get('/reports/inventory'),
  getStaffSalesReport: (params) => apiClient.get('/reports/staff-sales', { params }),
  getDashboardWidgets: () => apiClient.get('/reports/dashboard-widgets'),
  
  // Backup & Restore
  backup: () => apiClient.get('/backup'),
  restore: (data, clearExisting = false) => apiClient.post('/restore', { data, clear_existing: clearExisting }),
  
  // Serial Lookup
  lookupSerial: (serial) => apiClient.get(`/serials/lookup/${serial}`)
};

export default apiClient;
